/**
 * Auto-generated flight-info footer appended to every dispatch message.
 * Data comes from /api/acars/active (live) + optional /api/ofp/latest.
 *
 * Format is intentionally CPDLC / ACARS style — monospace, aligned rows —
 * so it renders cleanly in Discord AND in the Dispatcher UI.
 */

const FOOTER_MARK_START = "\n\n---- FLIGHT INFO ----";
const FOOTER_MARK_END   = "---------------------";

/** Strip any previously appended footer so we never double-stamp. */
export function stripFlightFooter(text) {
  if (!text) return "";
  const idx = text.indexOf(FOOTER_MARK_START);
  return idx >= 0 ? text.slice(0, idx).replace(/\s+$/, "") : text;
}

/**
 * Build the footer from live ACARS + optional OFP.
 * Returns an empty string when there is no active flight.
 */
export function buildFlightFooter(active, ofp) {
  if (!active) return "";

  const flt = active.opr || active.operator
    ? `${active.opr || active.operator}${active.flt}`
    : String(active.flt || "");
  const type = active.type || "";
  const reg  = active.reg  || ofp?.aircraft?.reg || "";

  const lat = numToLat(active.pos_lat);
  const lon = numToLon(active.pos_long);
  const alt = active.alt ? `FL${String(Math.round(active.alt / 100)).padStart(3, "0")}` : "—";
  const spd = active.spd ? `${Math.round(active.spd)} kt` : "—";
  const hdg = active.hdg != null ? `${String(Math.round(active.hdg)).padStart(3, "0")}°` : "—";
  const vs  = active.vs  ? `${signed(Math.round(active.vs))} fpm` : "—";

  const fuel = active.fuel
    ? `${Number(active.fuel).toLocaleString()} kg`
    : ofp?.fuel?.plan_ramp
      ? `${Number(ofp.fuel.plan_ramp).toLocaleString()} kg (plan)`
      : "—";

  const sob = ofp?.weights?.pax_count
    ? `${Math.round(ofp.weights.pax_count)} + crew`
    : "—";

  const eta = computeEta(active, ofp);

  const lines = [
    FOOTER_MARK_START,
    `FLT    : ${flt}${type ? ` · ${type}` : ""}${reg ? ` · ${reg}` : ""}`,
    `ROUTE  : ${active.orig || "----"} → ${active.dest || "----"}`,
    `POS    : ${lat} ${lon}`,
    `LEVEL  : ${alt} · ${spd} · HDG ${hdg} · VS ${vs}`,
    `FUEL   : ${fuel}`,
    `SOB    : ${sob}`,
    `ETA    : ${eta}`,
    FOOTER_MARK_END,
  ];
  return lines.join("\n");
}

/**
 * Convenience: replace the footer inside an existing message body.
 *   const next = withFlightFooter(prevBody, active, ofp);
 * Safely round-trips — calling it repeatedly won't stack footers.
 */
export function withFlightFooter(body, active, ofp) {
  const stripped = stripFlightFooter(body);
  const footer = buildFlightFooter(active, ofp);
  return footer ? `${stripped}${footer}` : stripped;
}

// ---- helpers ---------------------------------------------------------------
function signed(n) {
  return (n > 0 ? "+" : "") + n;
}
function numToLat(v) {
  if (v == null || v === 0) return "----N";
  const n = Number(v);
  const hemi = n >= 0 ? "N" : "S";
  return `${Math.abs(n).toFixed(2)}${hemi}`;
}
function numToLon(v) {
  if (v == null || v === 0) return "-----E";
  const n = Number(v);
  const hemi = n >= 0 ? "E" : "W";
  return `${Math.abs(n).toFixed(2)}${hemi}`;
}
function computeEta(active, ofp) {
  // If already airborne, prefer OFP's remaining enroute time; else full ETE.
  const ete = Number(ofp?.times?.est_time_enroute);
  if (!ete) return "—";
  const t_off = Number(active.time_off || 0);
  const now = Math.floor(Date.now() / 1000);
  const elapsed = t_off ? Math.max(0, now - t_off) : 0;
  const remaining = Math.max(0, ete - elapsed);
  const eta = new Date((now + remaining) * 1000);
  const hh = String(eta.getUTCHours()).padStart(2, "0");
  const mm = String(eta.getUTCMinutes()).padStart(2, "0");
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  return `${hh}:${mm}Z (${h}h${String(m).padStart(2, "0")} to run)`;
}
